import { ArrowLeftIcon, ArrowLineDownIcon, CaretDownIcon, CheckIcon, TrashIcon } from '@phosphor-icons/react';
import axios from 'axios';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useCookies } from 'react-cookie';
import { toast } from 'react-hot-toast';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import {
  resetTimelineFilters,
  selectTimelineFilters,
  setTimelineFilters,
} from '../../../../../apps/frontend/src/redux/slices/filtersSlice';
import { Button } from '../../components/ui/button';
import { DropdownMenu, DropdownMenuContent, DropdownMenuTrigger } from '../../components/ui/dropdown-menu';
import { Label } from '../../components/ui/label';
import { RadioGroup, RadioGroupItem } from '../../components/ui/radio-group';
import { Skeleton } from '../../components/ui/skeleton';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../components/ui/table';
import { Tooltip, TooltipContent, TooltipTrigger } from '../../components/ui/tooltip';
import { AppLayout } from '../../Layout/AppLayout';
import { Tab, TabsContainer } from '../../utils/SubjectTabs';

const FILTER_OPTIONS = [
  { value: 'all', label: 'All' },
  { value: 'last10', label: 'Last 10 Tests' },
  { value: 'last5', label: 'Last 5 Tests' },
];

const ITEMS_PER_PAGE = 10;

const TimelineView = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { packId } = useParams();
  const [cookies] = useCookies();
  const dispatch = useDispatch();
  const timelineFilters = useSelector(selectTimelineFilters);

  const [selectedTestType, setSelectedTestType] = useState(timelineFilters.selectedTestType || 'All Tests');
  const [selectedFilter, setSelectedFilter] = useState(timelineFilters.selectedFilter || 'all');
  const [selectedSubject, setSelectedSubject] = useState(timelineFilters.selectedSubject || 'overall');

  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [tableData, setTableData] = useState([]);
  const [allTestsData, setAllTestsData] = useState([]);
  const [totalCount, setTotalCount] = useState(0);
  const [subjects, setSubjects] = useState([]);
  const [testTypeFilters, setTestTypeFilters] = useState([]);
  const [showSubjectHeader, setShowSubjectHeader] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const [showPredictedCol, setShowPredictedCol] = useState(false);
  const [predictedColTitle, setPredictedColTitle] = useState('');

  const [currentOffset, setCurrentOffset] = useState(0);
  const [hasMore, setHasMore] = useState(true);
  const [packInfo, setPackInfo] = useState(null);
  const observerTarget = useRef(null);
  const isFetchingRef = useRef(false);
  const observerRef = useRef(null);
  const scrollContainerRef = useRef(null);

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    const day = date.getDate();
    const month = date.toLocaleString('en-US', { month: 'short' });
    const year = date.getFullYear();
    return `${day} ${month} ${year}`;
  };

  const filterTestsBySubject = (tests, subjectId) => {
    if (subjectId === 'overall') {
      return tests;
    }
    return tests.filter((test) => {
      return test.testId?.subjects?.some((id) => id === subjectId);
    });
  };

  const fetchTimelineData = async (offset = 0, append = false) => {
    if (isFetchingRef.current) {
      return;
    }

    isFetchingRef.current = true;

    if (!append) {
      setLoading(true);
    } else {
      setLoadingMore(true);
    }

    try {
      let limit = ITEMS_PER_PAGE;
      let fetchOffset = offset;

      if (selectedFilter === 'last5') {
        limit = 5;
        fetchOffset = 0;
      } else if (selectedFilter === 'last10') {
        limit = 10;
        fetchOffset = 0;
      }

      const response = await axios.get(`/api/packs/${packId}/timeline-view`, {
        params: {
          testType: selectedTestType !== 'All Tests' ? selectedTestType : '',
          limit,
          offset: fetchOffset,
        },
        headers: { Authorization: `Bearer ${cookies['token']}` },
      });

      if (response && response.status === 200) {
        const {
          testsData,
          subjectMeta,
          filters,
          showSubjectTabs,
          totalTests,
          packTitle,
          examDetails,
          predictedColTitle,
          showPredictedCol,
        } = response.data.data;

        setShowPredictedCol(!!showPredictedCol);
        setPredictedColTitle(predictedColTitle || '');

        let transformedData = testsData.map((test) => ({
          _id: test._id,
          testName: test.testId?.title || 'Unnamed Test',
          dateOfAttempt: test.submittedAt,
          overallScore: `${test.totalMarkScored}/${test.testId?.totalMarks || 300}`,
          qsAttempted: `${test.totalAttempted}/${test.testId?.maxAttemptableQuestion || test.testId?.totalQuestions}`,
          accuracy: `${test.accuracy.toFixed(2)}%`,
          timeTaken: `${Math.floor(test.totalTimeTaken / 60)}/${test.testId?.totalTime || 180}`,
          marksLost: `${test.marksLost}/${test.testId?.totalMarks || 300}`,
          testId: test.testId?._id,
          rawTest: test,
          percentile: test.percentile,
        }));
        const transformedFilters = ['All Tests', ...filters];

        if (append) {
          setAllTestsData((prev) => {
            const newData = [...prev, ...transformedData];
            return newData;
          });
        } else {
          if (testsData && testsData.length > 0) {
            setAllTestsData(transformedData);
          } else {
            setAllTestsData([]);
          }
        }

        let updatedSubjects = [...subjects];
        for (const subj of subjectMeta || []) {
          const subjId = subj._id || subj.id;
          const exists = updatedSubjects.some((s) => (s._id || s.id) === subjId);
          if (!exists) {
            updatedSubjects.push(subj);
          }
        }
        updatedSubjects.sort((a, b) => {
          const order = { Overall: 0, Physics: 1, Chemistry: 2, Math: 3 };
          const aOrder = order[a.title] ?? 4;
          const bOrder = order[b.title] ?? 4;
          return aOrder - bOrder;
        });
        setSubjects(updatedSubjects);

        setTestTypeFilters(transformedFilters || []);

        if (!selectedTestType && transformedFilters.length > 0) {
          setSelectedTestType(transformedFilters[0]);
        }

        if (subjectMeta && updatedSubjects.length === 2 && selectedSubject === 'overall') {
          setSelectedSubject(updatedSubjects[1]._id);
        }

        setTotalCount(totalTests || 0);
        if (!packInfo) {
          setPackInfo({
            packTitle,
            packIcon: examDetails?.icon,
          });
        }

        const maxPossibleOffset = Math.floor((totalTests - 1) / ITEMS_PER_PAGE) * ITEMS_PER_PAGE;
        const currentTotal = append ? allTestsData.length + transformedData.length : transformedData.length;
        const moreAvailable = currentTotal < totalTests && fetchOffset < maxPossibleOffset;
        setHasMore(moreAvailable);

        const shouldShowTabs =
          selectedFilter === 'all' &&
          (showSubjectTabs ||
            selectedTestType === 'Topic & Chapter-wise Tests' ||
            selectedTestType === 'PYQ as Chapter-wise Tests');
        setShowSubjectHeader(shouldShowTabs);
      }
    } catch (error) {
      setAllTestsData([]);
      setTableData([]);
      setTotalCount(0);
      setHasMore(false);
    } finally {
      setLoading(false);
      setLoadingMore(false);
      isFetchingRef.current = false;
    }
  };

  useEffect(() => {
    if (allTestsData.length > 0) {
      const filteredTests = filterTestsBySubject(
        allTestsData.map((t) => t.rawTest),
        selectedSubject,
      );

      const transformedFiltered = filteredTests.map((test) => ({
        _id: test._id,
        testName: test.testId?.title || 'Unnamed Test',
        dateOfAttempt: test.submittedAt,
        overallScore: `${test.totalMarkScored}/${test.testId?.totalMarks || 300}`,
        qsAttempted: `${test.totalAttempted}/${test.testId?.maxAttemptableQuestion || test.testId?.totalQuestions}`,
        accuracy: `${test.accuracy.toFixed(2)}%`,
        timeTaken: `${Math.round(test.totalTimeTaken / 60)}/${test.testId?.totalTime || 180}`,
        marksLost: `${test.marksLost}/${test.testId?.totalMarks || 300}`,
        testId: test.testId?._id,
        percentile: test.percentile,
      }));
      setTableData(transformedFiltered);
    } else {
      setTableData([]);
      setTotalCount(0);
    }
  }, [allTestsData, selectedSubject]);

  const handleClearFilters = () => {
    if (allTestsData.length > 0) {
      setSelectedSubject('overall');
      dispatch(
        setTimelineFilters({
          selectedTestType,
          selectedFilter,
          selectedSubject: 'overall',
        }),
      );
      return;
    }
    const defaultTestType = testTypeFilters.length > 0 ? testTypeFilters[0] : 'All Tests';
    setLoading(true);
    setSelectedTestType(defaultTestType);
    setSelectedFilter('all');
    setSelectedSubject('overall');
    setCurrentOffset(0);
    setAllTestsData([]);
    setHasMore(true);
    dispatch(
      setTimelineFilters({
        selectedTestType: defaultTestType,
        selectedFilter: 'all',
        selectedSubject: 'overall',
      }),
    );
  };

  const handleDownload = async () => {
    setDownloading(true);
    try {
      const token = cookies['token'] || sessionStorage.getItem('token');
      if (!token) {
        toast.error('Authentication token not found. Please log in again.');
        return;
      }

      await axios.post(`/api/packs/${packId}/download-timeline-view`, {
        jwt: token,
      });

      toast.success(
        <div className="flex flex-col items-start">
          <p>Timeline PDF generation started.</p>
          <p>You will receive it on your email when ready.</p>
        </div>,
        {
          position: 'top-center',
          duration: 2000,
        },
      );
    } catch (error) {
      toast.error('Failed to start download. Please try again.', {
        position: 'top-center',
        duration: 1800,
      });
    } finally {
      setDownloading(false);
    }
  };

  useEffect(() => {
    if (packId) {
      setCurrentOffset(0);
      setHasMore(true);
      fetchTimelineData(0, false);
    }
  }, [packId, selectedTestType, selectedFilter]);

  const lastRowRef = useCallback(
    (node) => {
      if (observerRef.current) {
        observerRef.current.disconnect();
      }

      if (selectedFilter !== 'all') {
        return;
      }

      if (!node) {
        return;
      }

      if (!hasMore) {
        return;
      }

      if (!scrollContainerRef.current) {
        return;
      }

      const maxPossibleOffset = Math.floor((totalCount - 1) / ITEMS_PER_PAGE) * ITEMS_PER_PAGE;

      observerRef.current = new IntersectionObserver(
        (entries) => {
          if (entries[0].isIntersecting && hasMore && !isFetchingRef.current && currentOffset < maxPossibleOffset) {
            const newOffset = currentOffset + ITEMS_PER_PAGE;
            setCurrentOffset(newOffset);
            fetchTimelineData(newOffset, true);
          }
        },
        {
          root: scrollContainerRef.current,
          rootMargin: '200px',
          threshold: 0.1,
        },
      );

      observerRef.current.observe(node);
    },
    [hasMore, currentOffset, selectedFilter, selectedSubject, allTestsData.length, totalCount],
  );

  const handleBack = () => {
    dispatch(resetTimelineFilters());
    navigate('/');
  };

  const handleSubjectChange = (subjectId) => {
    if (subjectId === selectedSubject) return;
    setSelectedSubject(subjectId);
    dispatch(
      setTimelineFilters({
        selectedTestType,
        selectedFilter,
        selectedSubject: subjectId,
      }),
    );
  };

  const handleFilterChange = (filterValue) => {
    if (filterValue === selectedFilter) return;
    setLoading(true);
    setSelectedFilter(filterValue);
    setCurrentOffset(0);
    setHasMore(true);
    const newSubject = filterValue === 'last5' || filterValue === 'last10' ? 'overall' : selectedSubject;
    setSelectedSubject(newSubject);
    dispatch(
      setTimelineFilters({
        selectedTestType,
        selectedFilter: filterValue,
        selectedSubject: newSubject,
      }),
    );
  };

  const handleTestTypeChange = (value) => {
    if (value === selectedTestType) return;
    setLoading(true);
    setSelectedTestType(value);
    setSelectedFilter('all');
    setSelectedSubject('overall');
    setCurrentOffset(0);
    setHasMore(true);
    dispatch(
      setTimelineFilters({
        selectedTestType: value,
        selectedFilter: 'all',
        selectedSubject: 'overall',
      }),
    );
  };

  const handleRowClick = (testId) => {
    dispatch(
      setTimelineFilters({
        selectedTestType,
        selectedFilter,
        selectedSubject,
      }),
    );
    navigate(`/${packId}/${testId}/analysis?source=timeline_view`);
  };

  const activeSubject = subjects.find((s) => s.id === selectedSubject || s._id === selectedSubject);
  const tabColor = activeSubject?.color || 'var(--q3-accent-normal)';

  const shouldShowInfiniteScroll = selectedFilter === 'all';

  return (
    <AppLayout>
      <div className="w-full max-w-7xl h-screen mx-auto px-4 md:px-6 py-5 lg:py-6 flex flex-col">
        <div className="self-start w-full flex flex-row justify-between items-center">
          <button
            onClick={handleBack}
            className="p-2 mb-4 rounded-full text-[var(--q3-neutral-default)] bg-[var(--q3-surface-dimmer)] cursor-pointer transition-all duration-200 text-sm font-medium"
          >
            <ArrowLeftIcon className="w-4 h-4" weight="bold" />
          </button>
          <button
            onClick={handleDownload}
            disabled={downloading}
            className={`py-2 px-3 mb-4 rounded-full text-[var(--q3-neutral-default)] border border-[var(--q3-stroke-normal)] cursor-pointer transition-all duration-200 text-sm font-medium flex flex-row items-center gap-2 ${
              downloading ? 'opacity-50 cursor-not-allowed' : 'hover:bg-[var(--q3-surface-dim)]'
            }`}
          >
            <ArrowLineDownIcon className="w-4 h-4" weight="bold" />
            <span className="text-sm font-medium text-[var(--q3-neutral-default)]">
              {downloading ? 'Generating...' : 'Download'}
            </span>
          </button>
        </div>

        <div className="mb-2">
          <h1 className="text-xl md:text-2xl font-bold text-[var(--q3-neutral-default)] mb-1">Timeline View</h1>
          <p className="text-xs md:text-sm text-[var(--q3-neutral-light)] mb-4">
            {selectedFilter === 'all'
              ? 'This table shows all tests attempted.'
              : selectedFilter === 'last10'
                ? 'This table shows the last 10 tests attempted.'
                : selectedFilter === 'last5'
                  ? 'This table shows the last 5 tests attempted.'
                  : 'This table shows all tests attempted.'}
          </p>
          {packInfo ? (
            <div className="flex items-center gap-2 text-xs">
              <img src={packInfo && packInfo.packIcon} alt="packLogo" className="h-4 w-4" />
              <p className="text-[var(--q3-neutral-light)]">{packInfo && packInfo.packTitle}</p>
            </div>
          ) : (
            <div className="flex items-center gap-2">
              <Skeleton className="w-4 h-4 rounded-full " />
              <Skeleton className="w-[250px] h-4 cursor-pointer gap-2 rounded-4xl" />
            </div>
          )}
        </div>

        <div className="flex overflow-x-auto items-center gap-2 min-h-16 mb-2">
          {loading ? (
            <Skeleton className="w-[150px] h-[40px] cursor-pointer gap-2 px-4 py-2 rounded-4xl" />
          ) : (
            <Dropdown
              testTypeFilters={testTypeFilters}
              selectedTestType={selectedTestType}
              onValueChange={handleTestTypeChange}
            />
          )}

          <div className="h-6 border-l border-[var(--q3-stroke-normal)] flex-shrink-0" />

          {FILTER_OPTIONS.map((filter) => (
            <button
              key={filter.value}
              onClick={() => handleFilterChange(filter.value)}
              className={`flex-shrink-0 inline-flex cursor-pointer items-center gap-2 px-4 py-2 rounded-4xl border text-sm font-medium transition-all duration-200 ${
                selectedFilter === filter.value
                  ? 'bg-[var(--q3-neutral-default)] text-white border-[var(--q3-accent-normal)] shadow-sm'
                  : 'bg-[var(--q3-surface-default)] text-[var(--q3-neutral-default)] border-[var(--q3-stroke-normal)] hover:bg-[var(--q3-surface-dim)]'
              }`}
            >
              <span
                className={` ${selectedFilter === filter.value ? 'text-[var(--q3-neutral-default_inverted)]' : 'text-[var(--q3-neutral-default)]'}`}
              >
                {filter.label}
              </span>
              {selectedFilter === filter.value && (
                <CheckIcon
                  className="w-4 h-4"
                  weight="bold"
                  color={`${selectedFilter === filter.value ? 'var(--q3-neutral-default_inverted)' : 'var(--q3-neutral-default)'}`}
                />
              )}
            </button>
          ))}
        </div>

        <div
          className={`bg-[var(--q3-surface-default)] rounded-lg border border-[var(--q3-stroke-normal)] mb-12 lg:mb-0 flex flex-col min-h-0 ${tableData.length === 0 && 'h-full'}`}
        >
          {showSubjectHeader && (
            <div className="w-full overflow-x-auto rounded-t-lg">
              <TabsContainer color={tabColor}>
                {subjects.map((subject) => (
                  <Tab
                    key={subject.id || subject._id}
                    isActive={selectedSubject === (subject.id || subject._id)}
                    color={selectedSubject === (subject.id || subject._id) ? subject.color : 'var(--q3-neutral-light)'}
                    tabColor={subject.color}
                    onClick={() => handleSubjectChange(subject.id || subject._id)}
                    display={subjects.length === 2 && subject.id == 'overall' ? 'none' : 'flex'}
                  >
                    {subject.icon && <img src={subject.icon} alt={subject.title} className="h-6 w-6" />}
                    <p className="mb-0">{subject.title}</p>
                  </Tab>
                ))}
              </TabsContainer>
            </div>
          )}

          <div className={`relative flex-1 min-h-0 flex flex-col overflow-auto`} ref={scrollContainerRef}>
            {loading ? (
              <TableSkeleton />
            ) : tableData.length === 0 ? (
              <div className="flex-1 flex flex-col items-center justify-center gap-4">
                <p className="text-[var(--q3-neutral-light)] text-sm">{`No tests available for the selected ${allTestsData.length > 0 ? 'subject' : 'filters'}.`}</p>
                <Button
                  onClick={handleClearFilters}
                  className="flex items-center cursor-pointer gap-2 px-4 py-2 rounded-4xl border border-[var(--q3-stroke-normal)] bg-[var(--q3-danger-normal)] text-[var(--q3-neutral-default_inverted)] text-sm font-medium"
                >
                  <TrashIcon className="w-4 h-4" />
                  {allTestsData.length === 0 ? 'Reset Filters' : 'Clear Subject Filter'}
                </Button>
              </div>
            ) : (
              <>
                <Table>
                  <TableHeader className="bg-[var(--q3-surface-dim)] sticky top-0 z-10 rounded-lg">
                    <TableRow>
                      <TableHead className="text-left rounded-tl-lg text-sm font-semibold text-[var(--q3-neutral-default)] w-[220px] md:w-[280px] lg:w-[320px] h-16 px-4 bg-[var(--q3-surface-dim)] border-r border-[var(--q3-stroke-light)]">
                        Test Name
                      </TableHead>
                      <TableHead className="text-center text-sm font-semibold text-[var(--q3-neutral-default)] w-auto h-16 px-2 whitespace-nowrap bg-[var(--q3-surface-dim)] border-r border-[var(--q3-stroke-light)]">
                        Date of Attempt
                      </TableHead>
                      <TableHead className="text-center text-sm font-semibold text-[var(--q3-neutral-default)] w-auto h-16 px-2 whitespace-nowrap bg-[var(--q3-surface-dim)] border-r border-[var(--q3-stroke-light)]">
                        Overall Score
                      </TableHead>
                      <TableHead className="text-center text-sm font-semibold text-[var(--q3-neutral-default)] w-auto h-16 px-2 whitespace-nowrap bg-[var(--q3-surface-dim)] border-r border-[var(--q3-stroke-light)]">
                        Qs Attempted
                      </TableHead>
                      <TableHead className="text-center text-sm font-semibold text-[var(--q3-neutral-default)] w-auto h-16 px-2 whitespace-nowrap bg-[var(--q3-surface-dim)] border-r border-[var(--q3-stroke-light)]">
                        Accuracy
                      </TableHead>
                      <TableHead
                        className={`text-center ${showPredictedCol ? 'border-r border-[var(--q3-stroke-light)]' : 'rounded-tr-lg'} text-sm font-semibold text-[var(--q3-neutral-default)] w-auto h-16 px-2 whitespace-nowrap bg-[var(--q3-surface-dim)]`}
                      >
                        Time Taken
                      </TableHead>
                      {showPredictedCol && (
                        <TableHead className="text-center rounded-tr-lg text-sm font-semibold text-[var(--q3-neutral-default)] w-auto h-16 px-2 whitespace-nowrap bg-[var(--q3-surface-dim)] ">
                          {predictedColTitle || 'Predicted Percentile'}
                        </TableHead>
                      )}
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {tableData.map((row, index) => {
                      const dateParts = formatDate(row.dateOfAttempt).split(' ');
                      const isLastRow = index === tableData.length - 1;
                      return (
                        <React.Fragment key={row._id || `${selectedSubject}-${index}`}>
                          <TableRow
                            key={row._id || `${selectedSubject}-${index}`}
                            className="hover:bg-[var(--q3-surface-dim)] transition-colors duration-150 cursor-pointer border-b border-[var(--q3-stroke-light)]"
                            onClick={() => handleRowClick(row.testId)}
                          >
                            <TableCell className="text-left text-sm text-[var(--q3-neutral-default)] w-[220px] md:w-[280px] lg:w-[320px] h-auto px-4 border-r border-[var(--q3-stroke-light)] ">
                              <Tooltip>
                                <TooltipTrigger asChild>
                                  <div className="line-clamp-2 overflow-hidden w-[220px] md:w-[280px] lg:w-[320px] ">
                                    <span className="whitespace-normal">{row.testName}</span>
                                  </div>
                                </TooltipTrigger>
                                <TooltipContent>{row.testName}</TooltipContent>
                              </Tooltip>
                            </TableCell>
                            <TableCell className="text-center text-xs w-auto h-auto px-2 whitespace-nowrap border-r border-[var(--q3-stroke-light)]">
                              <span className="text-xs font-medium text-[var(--q3-neutral-default)]">
                                {dateParts[0]}
                              </span>
                              <span className="text-xs text-[var(--q3-neutral-light)]">
                                {' '}
                                {dateParts[1]} {dateParts[2]}
                              </span>
                            </TableCell>
                            <TableCell className="text-center text-xs w-auto h-auto px-2 whitespace-nowrap border-r border-[var(--q3-stroke-light)]">
                              <span className="text-xs font-medium text-[var(--q3-neutral-default)]">
                                {row.overallScore.split('/')[0]}
                              </span>
                              <span className="text-xs font-medium text-[var(--q3-neutral-light)]">
                                {' '}
                                / {row.overallScore.split('/')[1]}
                              </span>
                            </TableCell>
                            <TableCell className="text-center text-xs w-auto h-auto px-2 whitespace-nowrap border-r border-[var(--q3-stroke-light)]">
                              <span className="text-xs font-medium text-[var(--q3-neutral-default)]">
                                {row.qsAttempted.split('/')[0]}
                              </span>
                              <span className="text-xs font-medium text-[var(--q3-neutral-light)]">
                                {' '}
                                / {row.qsAttempted.split('/')[1]}
                              </span>
                            </TableCell>
                            <TableCell className="text-center text-xs w-auto h-auto px-2 whitespace-nowrap border-r border-[var(--q3-stroke-light)]">
                              <span className="text-xs font-medium text-[var(--q3-neutral-default)]">
                                {row.accuracy}
                              </span>
                            </TableCell>
                            <TableCell
                              className={`text-center text-xs w-auto h-auto px-2 whitespace-nowrap relative ${showPredictedCol && 'border-r border-[var(--q3-stroke-light)]'}`}
                            >
                              <span className="text-xs font-medium text-[var(--q3-neutral-default)]">
                                {row.timeTaken.split('/')[0]}
                              </span>
                              <span className="text-xs font-medium text-[var(--q3-neutral-light)]">
                                {' '}
                                / {row.timeTaken.split('/')[1]}mins
                              </span>
                            </TableCell>
                            {showPredictedCol && (
                              <TableCell className="text-center text-xs w-auto h-auto px-2 whitespace-nowrap">
                                <span className="text-xs font-medium text-[var(--q3-neutral-default)]">
                                  {row.percentile
                                    ? `${row.percentile?.title ?? ''} ${
                                        row.percentile?.data
                                          ? typeof row.percentile.data === 'string'
                                            ? row.percentile?.title
                                              ? row.percentile.data
                                              : parseFloat(row.percentile.data).toFixed(2) + '%ile'
                                            : row.percentile.data.toFixed(2) + '%ile'
                                          : ''
                                      }`
                                    : '-'}
                                </span>
                              </TableCell>
                            )}
                          </TableRow>
                          {isLastRow && shouldShowInfiniteScroll && (
                            <TableRow className="rounded-b-lg">
                              <TableCell className="relative p-0" colSpan={7}>
                                <div
                                  ref={lastRowRef}
                                  className="absolute bottom-0 left-0 w-full h-1 "
                                  data-observer-target="last-row"
                                  style={{ pointerEvents: 'none' }}
                                />
                              </TableCell>
                            </TableRow>
                          )}
                        </React.Fragment>
                      );
                    })}
                  </TableBody>
                </Table>
                {loadingMore && (
                  <div className="w-full py-3 flex items-center justify-center border-t border-[var(--q3-stroke-light)]">
                    <div className="flex items-center gap-3">
                      <div className="relative w-5 h-5">
                        <div className="absolute inset-0 border-2 border-[var(--q3-stroke-normal)] rounded-full"></div>
                        <div className="absolute inset-0 border-2 border-[var(--q3-accent-normal)] border-t-transparent rounded-full animate-spin"></div>
                      </div>
                      <span className="text-xs font-medium text-[var(--q3-neutral-default)]">
                        Loading more tests...
                      </span>
                    </div>
                  </div>
                )}
              </>
            )}
          </div>
        </div>
      </div>
    </AppLayout>
  );
};

const Dropdown = ({ testTypeFilters, selectedTestType, onValueChange }) => {
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button className="inline-flex cursor-pointer items-center justify-between gap-2 px-4 py-2 bg-[var(--q3-accent-light)] rounded-4xl border border-[var(--q3-accent-normal)] text-[var(--q3-accent-normal)] transition-all duration-200 text-sm font-medium whitespace-nowrap">
          <span>{selectedTestType || testTypeFilters[0]}</span>
          <CaretDownIcon size={16} className="transition-transform duration-200" />
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent className="w-auto bg-[var(--q3-surface-default)] border border-[var(--q3-stroke-normal)] rounded-md shadow-lg z-50 overflow-hidden">
        <RadioGroup value={selectedTestType} onValueChange={onValueChange}>
          {testTypeFilters.map((filter) => (
            <div
              key={filter}
              className="flex items-center gap-2 px-3 py-2 cursor-pointer hover:bg-[var(--q3-surface-dim)]"
              onClick={() => onValueChange(filter)}
            >
              <RadioGroupItem
                value={filter}
                id={filter}
                className="border-[2px] border-[var(--q3-neutral-default)] data-[state=checked]:border-[var(--q3-accent-normal)] data-[state=checked]:text-[var(--q3-accent-normal)]"
                color="var(--q3-accent-normal)"
              />
              <Label htmlFor={filter} className="text-[var(--q3-neutral-default)] text-sm cursor-pointer">
                {filter}
              </Label>
            </div>
          ))}
        </RadioGroup>
      </DropdownMenuContent>
    </DropdownMenu>
  );
};

const TableSkeleton = () => (
  <div className="overflow-x-auto">
    <Table>
      <TableHeader className="bg-[var(--q3-surface-dim)] sticky top-0 z-10 rounded-lg">
        <TableRow>
          <TableHead className="text-left rounded-tl-lg text-sm font-semibold w-[220px] md:w-[280px] lg:w-[320px] h-16 px-4 bg-[var(--q3-surface-dim)] border-r border-[var(--q3-stroke-light)]">
            <Skeleton className="h-4 w-24 bg-[var(--q3-stroke-normal)]" />
          </TableHead>
          <TableHead className="text-center text-sm font-semibold w-auto h-16 px-2 whitespace-nowrap bg-[var(--q3-surface-dim)] border-r border-[var(--q3-stroke-light)]">
            <Skeleton className="h-4 w-20 bg-[var(--q3-stroke-normal)] mx-auto" />
          </TableHead>
          <TableHead className="text-center text-sm font-semibold w-auto h-16 px-2 whitespace-nowrap bg-[var(--q3-surface-dim)] border-r border-[var(--q3-stroke-light)]">
            <Skeleton className="h-4 w-20 bg-[var(--q3-stroke-normal)] mx-auto" />
          </TableHead>
          <TableHead className="text-center text-sm font-semibold w-auto h-16 px-2 whitespace-nowrap bg-[var(--q3-surface-dim)] border-r border-[var(--q3-stroke-light)]">
            <Skeleton className="h-4 w-20 bg-[var(--q3-stroke-normal)] mx-auto" />
          </TableHead>
          <TableHead className="text-center text-sm font-semibold w-auto h-16 px-2 whitespace-nowrap bg-[var(--q3-surface-dim)] border-r border-[var(--q3-stroke-light)]">
            <Skeleton className="h-4 w-16 bg-[var(--q3-stroke-normal)] mx-auto" />
          </TableHead>
          <TableHead className="text-center text-sm font-semibold w-auto h-16 px-2 whitespace-nowrap bg-[var(--q3-surface-dim)] border-r border-[var(--q3-stroke-light)]">
            <Skeleton className="h-4 w-20 bg-[var(--q3-stroke-normal)] mx-auto" />
          </TableHead>
          <TableHead className="text-center rounded-tr-lg text-sm font-semibold w-auto h-16 px-2 whitespace-nowrap bg-[var(--q3-surface-dim)]">
            <Skeleton className="h-4 w-20 bg-[var(--q3-stroke-normal)] mx-auto" />
          </TableHead>
        </TableRow>
      </TableHeader>
      <TableBody>
        {[...Array(15)].map((_, i) => (
          <TableRow key={i} className="border-b border-[var(--q3-stroke-light)]">
            <TableCell className="text-left text-sm w-[220px] md:w-[280px] lg:w-[320px] h-auto px-4 border-r border-[var(--q3-stroke-light)]">
              <Skeleton className="h-5 w-full bg-[var(--q3-stroke-normal)]" />
            </TableCell>
            <TableCell className="text-center text-sm w-auto h-auto px-2 whitespace-nowrap border-r border-[var(--q3-stroke-light)]">
              <Skeleton className="h-5 w-20 bg-[var(--q3-stroke-normal)] mx-auto" />
            </TableCell>
            <TableCell className="text-center text-sm w-auto h-auto px-2 whitespace-nowrap border-r border-[var(--q3-stroke-light)]">
              <Skeleton className="h-5 w-16 bg-[var(--q3-stroke-normal)] mx-auto" />
            </TableCell>
            <TableCell className="text-center text-sm w-auto h-auto px-2 whitespace-nowrap border-r border-[var(--q3-stroke-light)]">
              <Skeleton className="h-5 w-16 bg-[var(--q3-stroke-normal)] mx-auto" />
            </TableCell>
            <TableCell className="text-center text-sm w-auto h-auto px-2 whitespace-nowrap border-r border-[var(--q3-stroke-light)]">
              <Skeleton className="h-5 w-12 bg-[var(--q3-stroke-normal)] mx-auto" />
            </TableCell>
            <TableCell className="text-center text-sm w-auto h-auto px-2 whitespace-nowrap border-r border-[var(--q3-stroke-light)]">
              <Skeleton className="h-5 w-16 bg-[var(--q3-stroke-normal)] mx-auto" />
            </TableCell>
            <TableCell className="text-center text-sm w-auto h-auto px-2 whitespace-nowrap">
              <Skeleton className="h-5 w-12 bg-[var(--q3-stroke-normal)] mx-auto" />
            </TableCell>
          </TableRow>
        ))}
      </TableBody>
    </Table>
  </div>
);

export default TimelineView;
